'use client'
import * as React from 'react'
import { ChallengeType } from 'codegen/types'
import type { UserAuthResponse } from '@repo/auth-utils'
import { getDeviceId, saveToken } from '@repo/auth-utils'
import { LockFasIcon } from 'primitives/icons/security'
import { MailFasIcon } from 'primitives/icons/communication'
import { EyeFasIcon, EyeSlashFasIcon } from 'primitives/icons/general'
import { CheckIcon, SpinnerIcon } from 'primitives/icons'
import * as Form from 'primitives/form'
import { tw } from 'utils/classnames'
import { isError } from 'utils/js'
import { useTranslations } from 'next-intl'
import { isLoggedInSelector } from 'auth/selectors'

import { Divider } from '../divider'
import { useUserAuthModal } from './user-auth-dialog-context'
import { SocialButtons } from './social-buttons'
import { useLogin } from './hooks/use-login'
import { getLastUsedMethod, saveLastUsedMethod } from './utils/track-registration'
import { LastUsedMarker } from './last-used-marker'

export function LoginForm({
  onRegister,
  onOtp,
  onLostPassword,
  onSuccess,
}: {
  onRegister(formState: FormData): void
  onOtp(formState: FormData): void
  onLostPassword(formState: FormData): void
  onSuccess(user: UserAuthResponse): void
}) {
  const t = useTranslations('login')
  const { formState, setErrorMessage } = useUserAuthModal()
  const [showPassword, setShowPassword] = React.useState(false)
  const rememberForm = formState?.get('remember')?.toString() === 'on'
  const [remember, setRemember] = React.useState(rememberForm)
  const [lastUsedMethod] = React.useState(getLastUsedMethod())

  const loginMutation = useLogin({ onSuccess })

  const ref = React.useRef<HTMLFormElement>(null)

  function onSubmit(e: React.FormEvent<HTMLFormElement>) {
    e.preventDefault()

    const formData = new FormData(ref.current!)

    const email = formData.get('email')?.toString() ?? ''
    const password = formData.get('password')?.toString() ?? ''
    const deviceId = getDeviceId()

    saveLastUsedMethod('email')

    loginMutation.mutate(
      {
        input: {
          email,
          deviceId,
          password,
          remember,
        },
      },
      {
        onSuccess: async function onSuccessCallback(data) {
          if (data.login?.challenge?.type === ChallengeType.ChallengeOtp) {
            if (data.login.challenge.otp) {
              formData.set('otp', data.login.challenge.otp?.toString() ?? '')
            }

            onOtp(formData)
          } else if (data.login?.token) {
            await saveToken(data.login.token, 'login').catch(() => {
              throw new Error('CF_INVALID_TOKEN')
            })
          }
        },
      },
    )
  }

  function onRegisterClick(e: React.MouseEvent<HTMLButtonElement>) {
    e.preventDefault()
    const form = new FormData(ref.current!)
    onRegister(form)
  }

  function onLostPasswordClick(e: React.MouseEvent<HTMLButtonElement>) {
    e.preventDefault()
    const form = new FormData(ref.current!)
    onLostPassword(form)
  }

  React.useEffect(() => {
    function resolveErrorMessage() {
      if (loginMutation.isPending || !loginMutation.isError) {
        return null
      }

      if (loginMutation.isPaused) {
        setErrorMessage(t('errors.offline'))
        return
      }

      if (isError(loginMutation.error) && loginMutation.error.message === 'CF_INVALID_USER') {
        setErrorMessage(
          t.rich('errors.invalid_details', {
            email: () => <b className="break-all">{loginMutation.variables?.input?.email}</b>,
            forgetPassword: str => (
              <span
                onClick={onLostPasswordClick}
                className="text-14 m-auto mt-4 cursor-pointer font-bold text-[#ffffff] underline hover:text-[#ffffff]"
              >
                {str}
              </span>
            ),
          }),
        )
        return
      }

      setErrorMessage(t.rich('errors.general', { br: () => <br /> }))
    }

    resolveErrorMessage()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [loginMutation.isError, loginMutation.isPending, loginMutation.isPaused, loginMutation.error])

  const disabled =
    loginMutation.isPending ||
    isLoggedInSelector(loginMutation.data?.login) ||
    loginMutation.data?.login?.challenge?.type === ChallengeType.ChallengeOtp

  return (
    <div className="flex w-full flex-col">
      <h2 className="text-20 mb-4 text-center font-bold">{t('title')}</h2>
      <SocialButtons modalType="login" />
      <div className="mt-4 flex w-full items-center gap-1">
        <div className="h-[1px] w-full bg-[#e3e1e9]" />
        <div className="text-12 text-[#a399b8]">{t('or')}</div>
        <div className="h-[1px] w-full bg-[#e3e1e9]" />
      </div>
      <Form.Root
        ref={ref}
        onSubmit={onSubmit}
        className="mt-4 flex w-full flex-col gap-4"
        method="POST"
      >
        <Form.Field name="email">
          <Form.Label className="text-12 align-text-top data-[invalid='true']:text-red-100">
            {t('email')} *
          </Form.Label>
          <Form.Message match="valueMissing">{''}</Form.Message>
          <Form.FormMessage match="typeMismatch">{''}</Form.FormMessage>
          <div className="relative flex w-full rounded border border-[#e0e2ea] pl-[10px]">
            <div className="pointer-events-none absolute inset-y-0 flex items-center">
              <MailFasIcon className="h-[14px] w-[14px] text-[#e3e1e9]" />
            </div>
            <Form.Control asChild>
              <input
                required
                defaultValue={formState?.get('email')?.toString()}
                autoComplete="email"
                type="email"
                className="text-16 lg:text-14 h-[38px] w-full p-3 pl-7 outline-none"
                disabled={loginMutation.isPending}
              />
            </Form.Control>

            {lastUsedMethod === 'email' ? (
              <div className="flex items-center">
                <LastUsedMarker />
              </div>
            ) : null}
          </div>
        </Form.Field>
        <Form.Field name="password">
          <Form.Label className="text-12 align-text-top data-[invalid='true']:text-red-100">
            {t('password')} *
          </Form.Label>
          <Form.Message match="valueMissing">{''}</Form.Message>
          <div className="relative flex w-full rounded border border-[#e0e2ea] pl-[10px]">
            <div className="pointer-events-none absolute inset-y-0 flex items-center">
              <LockFasIcon className="h-[14px] w-[14px] text-[#e3e1e9]" />
            </div>
            <Form.Control asChild>
              <input
                required
                autoComplete="current-password"
                defaultValue={formState?.get('password')?.toString()}
                type={showPassword ? 'text' : 'password'}
                className="text-16 lg:text-14 h-[38px] w-full py-3 pl-7 pr-[60px] outline-none"
                disabled={disabled}
              />
            </Form.Control>
            <div
              onClick={() => {
                setShowPassword(prevValue => !prevValue)
              }}
              className="pointer-events absolute inset-y-0 right-3 flex items-center"
            >
              {showPassword ? (
                <EyeSlashFasIcon className="h-[16px] w-[16px] text-[#a399b8]" />
              ) : (
                <EyeFasIcon className="h-[14px] w-[14px] text-[#a399b8]" />
              )}
            </div>
          </div>
        </Form.Field>
        <Form.Field
          onClick={() => setRemember(prevValue => !prevValue)}
          name="remember"
          className="flex cursor-pointer items-center gap-2"
        >
          <Form.Control asChild>
            <div className="">
              <div
                className={tw(
                  remember ? 'bg-[#0a5ea8]' : '',
                  'flex h-[16px] w-[16px] items-center justify-center rounded border-2 border-[#0a5ea8] text-[#ffffff]',
                )}
              >
                {remember ? <CheckIcon /> : null}
              </div>
              <input
                checked={remember}
                type="checkbox"
                name="remember"
                className="hidden opacity-0"
                readOnly
              />
            </div>
          </Form.Control>
          <Form.Label className="text-14 cursor-pointer">{t('remember')}</Form.Label>
        </Form.Field>

        <Form.Submit asChild>
          <button
            disabled={disabled}
            className="text-14 bg-secondary-500 hover:bg-secondary-700 mt-4 flex h-[45px] w-full items-center justify-center rounded font-bold uppercase text-[#ffffff] transition-[background-color]"
          >
            {disabled ? <SpinnerIcon className="h-[24px] w-[24px] animate-spin" /> : t('cta')}
          </button>
        </Form.Submit>
      </Form.Root>
      <span
        onClick={onLostPasswordClick}
        className="text-14 hover:text-secondary-500 m-auto mt-4 cursor-pointer text-[#0a5ea8] underline"
      >
        {t('forgot_password')}
      </span>
      <Divider className="mt-2 bg-[#DFDDE5]" />
      <div className="text-14 m-auto mt-4">
        {t.rich('register', {
          register: str => (
            <button
              onClick={onRegisterClick}
              type="button"
              className="text-14 hover:text-secondary-500 cursor-pointer border-0 bg-transparent p-0 font-bold text-[#0a5ea8] underline outline-none focus-visible:outline"
            >
              {str}
            </button>
          ),
        })}
      </div>
    </div>
  )
}
